import React, { Suspense, useCallback, useMemo } from 'react';
import {
  ModelScope,
  ReadonlyWatchable,
  useAction,
  useInScopeModel,
  useWatchableValue,
} from '../../actions/framework';
import { GraphModel } from '../../graph/graph.model';
import { Assert } from '@cotera/utilities';
import { ColumnModelContext, useColumnModel } from './context';
import { Relation } from '@cotera/era';
import { useTabParams } from '../../tab-manager';
import { ErrorBoundary } from 'react-error-boundary';
import {
  DisplayError,
  Icon,
  Loading,
  Title,
  Tooltip,
  useModalManager,
} from '@cotera/client/app/components';
import { Text } from '@cotera/client/app/components/ui';
import { useEraScopesAwareRelIR } from '@cotera/client/app/pages/apps/compiler/macro-expansion/scopes';
import { Skeleton } from '../../components/ui/skeleton';
import { ToolConnections } from '../../components/tool-connections/tool-connections-dialog';
import { useToolSchemaLookup } from '@cotera/client/app/hooks/use-tool-schema';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../../components/ui/resizable';
import { ToolCallModel } from './tool-call.model';
import { BaseCellRenderer, GraphDataGrid, Sort } from '../../data-grid';
import { DetailViewCell } from '../../data-grid/detail-view';
import { CellComponentProps } from '../../components/data-grid';
import { Button } from '../../components/ui/button';
import { SaveToolCallColumnAction } from './actions/save-tool-call-column.action';
import { SchedulePopover } from './components/schedule';
import {
  scheduleDescription,
  getScheduleContextFromEventStream,
} from '../../utils';
import { useWhoami } from '@cotera/client/app/stores/org';
import { AppTab } from '../../components/tab/tab';
import { cn } from '../../lib/utils';
import { ToolCallColumnPreviewAction } from './actions/run-tool-call-column-preview.action';
import { RunStatus } from '../../components/run-status';
import { useRegisterInWindow } from '@cotera/client/app/hooks/use-register-in-window';
import { ErodSchemaForm } from './erod-schema-form/erod-schema-form';
import { FormValues } from './erod-schema-form/form-values';
import { ToolCallRunColumnAction } from './actions/run-tool-call-column.action';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { DeleteColumnAction } from '../common-actions/delete-column.action';
import { DuplicateAction } from '../../graph/duplicate.action';

const ColumnEditView: React.FC<{
  dataset: string;
  column: string;
  isNew: boolean;
}> = ({ dataset, column, isNew }) => {
  const graphModel = useInScopeModel(GraphModel);
  const toolSchemaLookup = useToolSchemaLookup();

  Assert.assert(graphModel !== null);

  const model = useMemo(
    () =>
      new ToolCallModel(
        {
          datasetName: dataset,
          columnName: column,
          graph: graphModel.graph,
          compiler: graphModel.compiler,
          toolSchemaLookup,
        },
        { targeted: true, isNew }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataset, column]
  );

  useRegisterInWindow('TOOL_CALL_MODEL', model);

  return (
    <AppTab model={model}>
      <ModelScope models={[model]}>
        <ColumnModelContext.Provider value={model}>
          <Main />
        </ColumnModelContext.Provider>
      </ModelScope>
    </AppTab>
  );
};

const Main: React.FC = () => {
  return <SelectTool />;
};

const SelectTool: React.FC = () => {
  const model = useColumnModel();
  const formState = useWatchableValue(model.formState);
  const selectedTool = formState.tool;
  const modalManager = useModalManager((s) => s.actions);
  const toolSchemaLookup = useToolSchemaLookup();

  return (
    <div className="flex flex-col px-4 py-4 w-full gap-4 h-full">
      {selectedTool === null && (
        <>
          <Text.Title
            type="section"
            subtitle="Choose a tool to use in your workflow"
          >
            Select a tool
          </Text.Title>
          <Suspense
            fallback={
              <div className="flex flex-col gap-2 mb-2 flex-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8" />
                ))}
              </div>
            }
          >
            <ToolConnections
              onSelect={async (tool) => {
                const toolSchema = toolSchemaLookup(tool.name);
                model.formState.set({
                  ...formState,
                  tool: toolSchema && {
                    name: tool.name,
                    id: tool.id,
                    description: tool.displayName,
                    method: toolSchema.method,
                    schema: toolSchema.inputSchema,
                    output: toolSchema.outputSchema,
                  },
                });
                modalManager.close();
              }}
              allowToolSelection={true}
              className="w-full"
              toolsClassName="mb-12"
            />
          </Suspense>
        </>
      )}
      {selectedTool && <ToolForm tool={selectedTool} />}
    </div>
  );
};

const Container: React.FC<{
  left: React.ReactNode;
  right: React.ReactNode;
  bottom: React.ReactNode;
}> = ({ left, right, bottom }) => {
  const model = useColumnModel();
  const preview = useWatchableValue(model.preview);

  return (
    <ResizablePanelGroup direction="vertical" className="h-full">
      <div className="flex flex-col py-4 h-full w-full">
        <ResizablePanel
          className={cn(
            'flex flex-col gap-4 w-full md:flex-row flex-1',
            preview === null ? 'h-full' : 'h-1/2'
          )}
        >
          <div className="flex flex-col gap-4 h-full md:w-1/2 w-full overflow-y-auto">
            {left}
          </div>
          <div className="flex flex-col gap-4 h-full md:w-1/2 w-full overflow-y-auto">
            {right}
          </div>
        </ResizablePanel>
        <ResizableHandle className="my-4" />
        {preview !== null && (
          <ResizablePanel className="flex flex-col gap-4 h-1/2 w-full overflow-y-auto flex-1">
            {bottom}
          </ResizablePanel>
        )}
      </div>
    </ResizablePanelGroup>
  );
};

const ToolForm: React.FC<{
  tool: {
    id: string;
    name: string;
    description: string;
  };
}> = ({ tool }) => {
  const model = useColumnModel();
  const datasetRel = useWatchableValue(model.datasetRel);
  const toolState = useWatchableValue(model.formState).tool;
  // const trackChange = useTextFieldTracking('Tool Parameters Typed'); // TODO: Re-enable tracking

  Assert.assert(toolState !== null, 'Tool not found');
  Assert.assert(toolState.schema !== null, 'Tool params schema not found');
  Assert.assert(toolState.output !== null, 'Tool output schema not found');

  const attrs = useMemo(() => {
    return (
      datasetRel?.match(
        (x) => x.rel.attributes,
        () => ({})
      ) ?? {}
    );
  }, [datasetRel]);

  const onChange = useCallback(
    (values: FormValues) => {
      model.formState.set({
        ...model.formState.snapshot(),
        parameters: values,
      });
    },
    [model.formState]
  );

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b pb-4 border-border">
        <div className="flex items-center gap-2">
          <Text.Title type="section" subtitle={tool.description}>
            {tool.name}
          </Text.Title>
          <RunStatus model={model} />
        </div>
        <Actions />
      </div>
      <Container
        left={
          <div className="flex flex-col gap-8 overflow-y-auto">
            <ErodSchemaForm
              schema={toolState.schema}
              initialFormValues={
                model.formState.snapshot().parameters ?? undefined
              }
              onChange={onChange}
              lintingParser={model.inputs.graph
                .snapshot()
                .parser.forRel(model.datasetName)}
              colRefAttrs={attrs}
            />
          </div>
        }
        right={
          <div className="flex flex-col gap-4">
            <Settings />
            <Text.Title
              type="label"
              subtitle="The tool call will return a value with this schema"
            >
              Output Schema
            </Text.Title>
            <pre className="text-sm bg-muted p-2 rounded-md overflow-x-auto">
              {(
                toolState.output.get('output') ?? toolState.output
              ).prettyPrint()}
            </pre>
          </div>
        }
        bottom={<PreviewSection />}
      />
    </>
  );
};

const Settings: React.FC = () => {
  const model = useColumnModel();
  const org = useWhoami((s) => s.org);
  const when = useWatchableValue(model.formState).when;
  const eventStream = useWatchableValue(model.eventStream);

  // Get context from event stream (same logic as backend)
  const scheduleContext = eventStream
    ? getScheduleContextFromEventStream(org.id, eventStream)
    : undefined;

  return (
    <div className="flex flex-col gap-2 w-full border-b pb-4 border-border">
      <div className="flex gap-2 items-center justify-between w-full">
        <Text.Title type="label">Settings</Text.Title>
        <SchedulePopover />
      </div>
      <span className="text-sm text-foreground/80">
        {scheduleDescription(when, 'tool', scheduleContext)}
      </span>
    </div>
  );
};

const Actions: React.FC = () => {
  const model = useColumnModel();
  const saveTool = useAction(SaveToolCallColumnAction);
  const errors = useWatchableValue(model.errors);
  const saveEnabled = Object.values(errors).every((k) => k.isOk());
  const playAction = useAction(ToolCallColumnPreviewAction);
  const runColumn = useAction(ToolCallRunColumnAction);
  const duplicateColumn = useAction(DuplicateAction);
  const deleteColumn = useAction(DeleteColumnAction);

  return (
    <div className="flex gap-2 group/actions items-center">
      <Tooltip side="right" tooltipContent="Preview">
        <Button
          variant="primary"
          size="icon"
          onClick={() => playAction.execute({})}
        >
          <Icon icon="play" />
        </Button>
      </Tooltip>
      <div className="flex items-center">
        <Button
          variant="secondary"
          className="rounded-r-none!"
          onClick={() => runColumn.execute()}
        >
          <Icon icon="zap" />
          Run Column
        </Button>
      </div>
      <Button
        variant="outline"
        disabled={!saveEnabled}
        onClick={async () => {
          await saveTool.execute();
        }}
      >
        <Icon icon="save" />
        Save
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size={'icon-sm'}>
            <Icon icon="ellipsis-vertical" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          <DropdownMenuItem onSelect={() => duplicateColumn.execute()}>
            <Icon icon="copy" />
            Duplicate Column
            <DropdownMenuShortcut shortcut={duplicateColumn.action?.shortcut} />
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => deleteColumn.execute()}>
            <Icon icon="trash" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const PreviewSection: React.FC = () => {
  const model = useColumnModel();
  const preview = useWatchableValue(model.preview);

  if (preview === null) {
    return null;
  }

  return (
    <div className="flex flex-col w-full h-full pb-4">
      <Title className="mb-2">Preview</Title>
      {preview === null && (
        <div>
          <Text.Caption>Run the source EraQL to see a preview</Text.Caption>
        </div>
      )}
      {preview !== null && <PreviewRel />}
    </div>
  );
};

const Cell = (
  props: CellComponentProps & {
    targetCol: string;
    rel: Relation;
    model: {
      sort: ReadonlyWatchable<Sort[]>;
    };
    columnModel: ToolCallModel;
  }
) => {
  // if (props.column.key === props.targetCol) {
  //   return (
  //     <DefaultCell {...props}>
  //       <ToolCallCell {...props} model={props.columnModel} />
  //     </DefaultCell>
  //   );
  // }
  return <BaseCellRenderer {...props} />;
};

const PreviewRel: React.FC = () => {
  const model = useColumnModel();
  const previewRel = useWatchableValue(model.preview)!;
  const relIr = useEraScopesAwareRelIR(previewRel);
  const builtRel = Relation.wrap(relIr);

  const CellRenderer = useMemo(() => {
    return (
      props: CellComponentProps<any> & {
        rel: Relation;
        model: {
          sort: ReadonlyWatchable<Sort[]>;
        };
      }
    ) => {
      return (
        <Cell
          {...props}
          targetCol={model.currentNameInGraph.snapshot()}
          columnModel={model}
        />
      );
    };
  }, [model]);

  const DetailViewCellRenderer = useMemo(
    () => {
      return React.forwardRef<
        HTMLDivElement,
        {
          column: string;
          value: any;
          rel: Relation;
          focused: boolean;
        }
      >((props, ref) => {
        // if (props.column === model.currentNameInGraph.snapshot()) {
        //   return (
        //     <DefaultDetailViewCell title={props.column} focused={props.focused}>
        //       <ToolCallCell {...props} model={model} />
        //     </DefaultDetailViewCell>
        //   );
        // }
        return <DetailViewCell {...props} ref={ref} focused={props.focused} />;
      });
    },
    [
      // model
    ]
  );

  return (
    <div className="w-full h-full">
      <GraphDataGrid
        rel={builtRel}
        relName={model.id}
        shouldShow={true}
        CellComponent={CellRenderer}
        DetailViewCell={DetailViewCellRenderer}
      />
    </div>
  );
};

export const NewToolCallColumn: React.FC = () => {
  const { id: dataset } = useTabParams<{ id: string }>();

  return (
    <SuspensefulColumnEditView
      dataset={dataset}
      column="new-column"
      isNew={true}
    />
  );
};

export const EditToolCallColumn: React.FC = () => {
  const { id: dataset, columnId: column } = useTabParams<{
    id: string;
    columnId: string;
  }>();
  return (
    <SuspensefulColumnEditView
      dataset={dataset}
      column={column}
      isNew={false}
    />
  );
};

const SuspensefulColumnEditView: React.FC<{
  dataset: string;
  column: string;
  isNew: boolean;
}> = ({ dataset, column, isNew }) => {
  return (
    <ErrorBoundary
      fallbackRender={(e) => (
        <DisplayError
          error={{
            message: JSON.stringify(e.error),
          }}
        />
      )}
    >
      <Suspense fallback={<Loading.Dots />}>
        <ColumnEditView dataset={dataset} column={column} isNew={isNew} />
      </Suspense>
    </ErrorBoundary>
  );
};
